import { useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, Trash2, PenLine } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SignaturePad } from "@/components/SignaturePad";

export const SignatureDialog = ({ open, onOpenChange }) => {
  const { user, setUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const src = user.has_signature ? `${process.env.REACT_APP_BACKEND_URL}/api/users/${user.id}/signature?v=${user.signature_updated_at}` : null;

  const upload = async (file) => {
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/webp"].includes(file.type)) return toast.error("Upload a PNG, JPG or WEBP image");
    if (file.size > 1_000_000) return toast.error("Signature image must be under 1 MB");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/me/signature", fd);
      setUser(r.data);
      toast.success("Signature saved — it will print on your receipts");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      const r = await api.delete("/me/signature");
      setUser(r.data);
      toast.success("Signature removed");
    } catch { toast.error("Could not remove"); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="signature-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">My signature</DialogTitle>
          <DialogDescription>Add it once — it prints automatically above the counsellor signature line on every receipt you issue.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-3 rounded-md border border-plum-900/10 bg-plum-50/60 p-2" data-testid="signature-preview">
          <div className="grid h-16 flex-1 place-items-center rounded bg-white">
            {src ? <img src={src} alt="Your signature" className="max-h-14 max-w-[90%] object-contain mix-blend-multiply" data-testid="signature-preview-img" />
              : <span className="flex items-center gap-1.5 text-xs text-slate-500"><PenLine className="h-4 w-4" />No signature saved yet</span>}
          </div>
          {src && <Button size="sm" variant="ghost" onClick={remove} disabled={busy} className="text-red-600" data-testid="signature-remove-btn"><Trash2 className="mr-1.5 h-4 w-4" />Remove</Button>}
        </div>
        <Tabs defaultValue="draw">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="draw" data-testid="signature-tab-draw"><PenLine className="mr-2 h-4 w-4" />Draw</TabsTrigger>
            <TabsTrigger value="upload" data-testid="signature-tab-upload"><Upload className="mr-2 h-4 w-4" />Upload</TabsTrigger>
          </TabsList>
          <TabsContent value="draw" className="mt-3">
            <SignaturePad onSave={upload} busy={busy} />
          </TabsContent>
          <TabsContent value="upload" className="mt-3 space-y-3">
            <p className="text-xs text-slate-500">Sign with a dark pen on plain white paper, take a clear photo, and crop it close to the signature. PNG, JPG or WEBP up to 1 MB.</p>
            <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="signature-file-input" />
            <div className="flex justify-end">
              <Button onClick={() => ref.current?.click()} disabled={busy} className="bg-plum-700 hover:bg-plum-800" data-testid="signature-upload-btn">
                {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}{src ? "Replace with image" : "Upload image"}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};
